import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useTranslation } from "react-i18next";
import SingleSelect from "../Select/SingleSelect";
import useGlobalData from "../../Hooks/useGlobalData";
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { GlobalStates } from '../../GlobalStates';

export const Rodoma = ({ 
    filters = {}, 
    setFilter, 
    user, 
    clearParam, 
    isLoading, 
    companies = [] 
}) => {
    const { t } = useTranslation();
    const { selectedCompany, setSelectedCompany } = useGlobalData();
    const { type } = useContext(GlobalStates);
    
    // Tikslesnis admin patikrinimo logika - pataisyta type patikra
    const isAdmin = user?.adminConfirmed === true || user?.admin_confirmed === true || type === 'Admin'; // Pakeista iš type === 0
    
    // Lokali būsena kasPildo reikšmei - užtikrina, kad UI iškart reaguos
    const [localKasPildo, setLocalKasPildo] = useState(
        filters.kasPildo === 'All' && !isAdmin ? 'Darbuotojo forma' : filters.kasPildo
    );
    
    // Lokali būsena įmonių pasirinkimui
    const [localCompany, setLocalCompany] = useState(() => {
        return selectedCompany || filters.imonesPavadinimas || '';
    });
    
    // Optimizacija: formatuojame įmonių sąrašą su useMemo, pridedame "Rodyti visus" opciją
    const formattedCompanies = useMemo(() => {
        if (!Array.isArray(companies) || companies.length === 0) return [];
        const uniqueCompanies = [...new Set(companies.filter(Boolean))].sort();
        return uniqueCompanies;
    }, [companies]);

    // Kai pasikeičia filtrai, atnaujiname lokalias reikšmes
    useEffect(() => {
        setLocalKasPildo(filters.kasPildo === 'All' && !isAdmin ? 'Darbuotojo forma' : filters.kasPildo);
    }, [filters.kasPildo, isAdmin]);
    
    // Sync local company with global state, but handle clearing properly
    useEffect(() => {
        const newValue = selectedCompany || filters.imonesPavadinimas || '';
        if (localCompany !== newValue) {
            setLocalCompany(newValue);
        }
    }, [selectedCompany, filters.imonesPavadinimas, localCompany]);

    // Nustatome pradinę pasirinktą įmonę tik kai komponentas užsikrauna
    useEffect(() => {
        if (formattedCompanies.length > 0 && filters.imonesPavadinimas && 
            formattedCompanies.includes(filters.imonesPavadinimas) && 
            selectedCompany !== filters.imonesPavadinimas) {
            setSelectedCompany(filters.imonesPavadinimas);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formattedCompanies, filters.imonesPavadinimas]);

    const handleCompanyChange = (event) => {
        const value = event.target.value;
        
        // Pirma tik atnaujiname lokalią reikšmę, kad UI reaguotų iškart
        setLocalCompany(value);
        
        // Optimizuotas įmonės pasirinkimo valdymas
        if (value === '') {
            // Jei pasirinkta "Visos įmonės", pašaliname įmonės pavadinimo filtrą
            setSelectedCompany(null);
            
            // Sukuriame naują filtrų objektą be imonesPavadinimas
            const newFilters = { ...filters };
            delete newFilters.imonesPavadinimas;
            
            // Atnaujiname filtrus
            setFilter(newFilters);
        } else {
            // Atnaujinti globalų state ir filtrus
            setSelectedCompany(value);
            setFilter({ ...filters, imonesPavadinimas: value });
        }
    };

    const handleKasPildoChange = (v, l) => {
        // Iškart atnaujiname lokalią reikšmę, kad vartotojo sąsaja reaguotų nedelsiant
        setLocalKasPildo(v);
        
        // Clear company selection when changing form type (for admins) - but keep global companies
        if (isAdmin) {
            setSelectedCompany(null);
            setLocalCompany('');
        }
        
        // Išvalome URL parametrus
        clearParam();
        
        // Perduodame naujus filtrus (without company filter when changing type)
        const newFilters = { ...filters, [l]: v };
        if (isAdmin) {
            delete newFilters.imonesPavadinimas;
        }
        setFilter(newFilters);
    };

    return (
        <div className="row verticalCenter tendencijos tendencijosBorderBot tendencijosSelect">
            <p>{t('rodoma')}</p>
            
            {/* Select'as pildytojo tipui pasirinkti */}
            <SingleSelect
                classNameBox={'rezRodomaMob'}
                classNameForm={'rezRodomaMob'}
                className='rezRodoma'
                classNameSelectLabel='rezRodomaLabel'
                options={t('pildytojuTipai', { returnObjects: true }).filter(opt => opt !== 'All')}
                optionsText={t('pildytojuTipaiText', { returnObjects: true }).filter((_, i) => 
                    t('pildytojuTipai', { returnObjects: true })[i] !== 'All'
                )}
                label={t('pildytojuTipaiLabel')}
                filterTag={'kasPildo'}
                size='small'
                value={localKasPildo} // Naudojame lokalią reikšmę
                set={handleKasPildoChange}
                disabled={isLoading} // Blokuojame, tik jei iš tikrųjų krauna
            />
            
            {/* Paprastas įmonių dropdown Select vietoj Autocomplete */}
            {/* SVARBUS PAKEITIMAS: Naudojame isAdmin vietoj tiesioginio patikrinimo */}
            {isAdmin ? (
                <FormControl
                    variant="outlined"
                    size="small"
                    sx={{ 
                        minWidth: 200,
                        ml: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                        },
                        // Pridedame papildomus stilius, kad išvengtume teksto persidengimo
                        '& .MuiInputBase-input': {
                            color: 'rgba(0, 0, 0, 0.87)',
                            zIndex: 1
                        },
                        '& .MuiFormLabel-root': {
                            backgroundColor: 'white',
                            padding: '0 4px',
                            zIndex: 2
                        }
                    }}
                    disabled={isLoading}
                >
                    <InputLabel id="company-select-label" sx={{ backgroundColor: 'white', padding: '0 4px' }}>{t('visosImonesLabel')}</InputLabel>
                    <Select
                        labelId="company-select-label"
                        value={localCompany}
                        onChange={handleCompanyChange}
                        label={t('visosImonesLabel')}
                        className="tendencijosSelect"
                        displayEmpty
                        sx={{ 
                            '& .MuiSelect-select': { 
                                padding: '8px 14px',
                                '&.Mui-disabled': {
                                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                                }
                            }
                        }}
                        renderValue={(selected) => {
                            if (!selected || selected === '') {
                                return <span style={{ display: 'block', color: '#0066CC', fontWeight: 'normal' }}>{t('rodytiVisus')}</span>;
                            }
                            return selected;
                        }}
                    >
                        <MenuItem value="">
                            <span style={{ color: '#0066CC', fontWeight: 'normal' }}>{t('rodytiVisus')}</span>
                        </MenuItem>
                        {formattedCompanies.map((company) => (
                            <MenuItem key={company} value={company}>
                                {company}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : (
                <div style={{ fontSize: '12px', color: 'gray', margin: '0 10px' }}>
                    {/* Pranešimas pašalintas */}
                </div>
            )}
        </div>
    );
};

export default Rodoma;